import { Component, OnInit, inject, signal } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ConfirmationService, MessageService, PrimeTemplate } from "primeng/api";
import { environment } from "../../../environments/environment";
import {
  PublicApiInfo,
  WebsiteSettingsService,
  WebsiteSite,
} from "../../services/website-settings.service";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { RouterLink } from "@angular/router";
import { DatePipe } from "@angular/common";
import { Tag } from "primeng/tag";
import { Tabs, TabList, Tab, TabPanels, TabPanel } from "primeng/tabs";
import { Ripple } from "primeng/ripple";
import { SelectButton } from "primeng/selectbutton";
import { Select } from "primeng/select";
import { Message } from "primeng/message";
import { InputText } from "primeng/inputtext";
import { TableModule } from "primeng/table";
import { ConfirmDialog } from "primeng/confirmdialog";
import { Toast } from "primeng/toast";
import { TranslatePipe } from "@ngx-translate/core";

import { TPipe } from "../../i18n/t.pipe";
import { opt } from "../../i18n/i18n";
@Component({
    standalone: true,
    selector: "app-website-settings",
    templateUrl: "./website-settings.component.html",
    styles: [`
    .status { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-bottom: 16px; }
    .status .tile { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px 16px; display: flex; gap: 12px; align-items: center; }
    .status .tile i { font-size: 1.4rem; color: #0f766e; }
    .status .tile .label { font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; }
    .status .tile .value { font-weight: 600; word-break: break-all; }
    .steps { list-style: none; margin: 0; padding: 0; counter-reset: step; }
    .steps > li { counter-increment: step; display: flex; gap: 14px; padding: 16px 0; border-top: 1px solid #e5e7eb; }
    .steps > li:first-child { border-top: 0; padding-top: 4px; }
    .steps > li::before { content: counter(step); flex: none; width: 28px; height: 28px; border-radius: 50%; background: #0f766e; color: #fff; font-weight: 700; display: flex; align-items: center; justify-content: center; }
    .steps .body { flex: 1; min-width: 0; }
    .steps h4 { margin: 2px 0 6px; font-size: 1rem; }
    .hint { color: #6b7280; font-size: 13px; margin: 6px 0 0; }
    .warn { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; border-radius: 6px; padding: 8px 12px; margin-top: 8px; font-size: 13px; }
    .code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; background: #111827; color: #e5e7eb; border-radius: 8px; padding: 14px; white-space: pre-wrap; word-break: break-all; margin: 0; }
    .keybox { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
  `],
    imports: [TPipe, Bind, ButtonDirective, RouterLink, Tag, Tabs, TabList, Ripple, Tab, TabPanels, TabPanel, SelectButton, FormsModule, Select, Message, ReactiveFormsModule, InputText, TableModule, PrimeTemplate, ConfirmDialog, Toast, DatePipe, TranslatePipe]
})
export class WebsiteSettingsComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private service = inject(WebsiteSettingsService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  readonly loading = signal(true);

  readonly tab = signal(0);
  keyModes = [
    opt("Sito registrato (senza chiave)", false),
    opt("Con chiave API", true),
  ];
  readonly keyError = signal("");
  readonly sitesError = signal("");

  readonly info = signal<PublicApiInfo | null>(null);
  readonly sites = signal<WebsiteSite[]>([]);

  readonly includeKey = signal(true);
  readonly snippetLang = signal("it");
  languages = [
    { label: "Italiano", value: "it" },
    { label: "English", value: "en" },
    { label: "Deutsch", value: "de" },
    { label: "Français", value: "fr" },
    { label: "Español", value: "es" },
  ];

  form: UntypedFormGroup;
  readonly adding = signal(false);

  ngOnInit(): void {
    this.form = this.fb.group({
      name: [""],
      origin: ["", [Validators.required, Validators.pattern(/^https?:\/\/[^\s/]+\S*$/i)]],
    });
    this.loadKey();
    this.loadSites();
  }

  get apiUrl(): string {
    return environment.baseUrl.replace(/\/+$/, "");
  }

  get snippet(): string {
    const key = this.includeKey() && this.info() ? `\n        data-key="${this.info().key}"` : "";
    return (
      `<div id="nexo-booking"></div>\n` +
      `<script src="${this.apiUrl}/assets/booking-widget.js"\n` +
      `        data-api="${this.apiUrl}"${key}\n` +
      `        data-target="#nexo-booking" data-lang="${this.snippetLang()}"></script>`
    );
  }

  loadKey(): void {
    this.service.getPublicApi().subscribe({
      next: (info) => {
        this.info.set(info);
        this.keyError.set("");
        this.loading.set(false);
      },
      error: (err) => {
        this.keyError.set(this.message(err));
        this.loading.set(false);
      },
    });
  }

  loadSites(): void {
    this.service.getSites().subscribe({
      next: (res) => {
        this.sites.set(res.sites);
        this.sitesError.set("");
      },
      error: (err) => (this.sitesError.set(this.message(err))),
    });
  }

  rotate(): void {
    this.confirmationService.confirm({
      header: "Genera una nuova chiave",
      message:
        "La chiave attuale smette di funzionare subito: i siti che la usano nello snippet vanno aggiornati. " +
        "I siti registrati qui sotto non sono interessati. Continuare?",
      icon: "pi pi-exclamation-triangle",
      accept: () =>
        this.service.rotatePublicApi().subscribe({
          next: (info) => {
            this.info.set(info);
            this.toast("success", "Nuova chiave generata");
          },
          error: (err) => this.toast("error", this.message(err)),
        }),
    });
  }

  addSite(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.adding.set(true);
    const { name, origin } = this.form.value;
    this.service.addSite({ name: (name || "").trim(), origin: origin.trim() }).subscribe({
      next: (res) => {
        this.sites.set(res.sites);
        this.form.reset({ name: "", origin: "" });
        this.adding.set(false);
        this.toast("success", "Sito aggiunto");
      },
      error: (err) => {
        this.adding.set(false);
        this.toast("error", this.message(err));
      },
    });
  }

  removeSite(site: WebsiteSite): void {
    this.confirmationService.confirm({
      header: "Rimuovi sito",
      message: `Il sito ${site.origin} non potrà più prenotare senza chiave. Continuare?`,
      icon: "pi pi-exclamation-triangle",
      accept: () =>
        this.service.deleteSite(site.id).subscribe({
          next: (res) => {
            this.sites.set(res.sites);
            this.toast("success", "Sito rimosso");
          },
          error: (err) => this.toast("error", this.message(err)),
        }),
    });
  }

  copy(text: string): void {
    const done = () => this.toast("success", "Copiato negli appunti");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => this.fallbackCopy(text, done));
    } else {
      this.fallbackCopy(text, done);
    }
  }

  private fallbackCopy(text: string, done: () => void): void {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    document.execCommand("copy");
    document.body.removeChild(area);
    done();
  }

  private toast(severity: "success" | "error", detail: string): void {
    this.messageService.add({
      key: "myKey1",
      severity,
      summary: severity === "success" ? "Ok" : "Errore",
      detail,
    });
  }

  private message(err: HttpErrorResponse): string {
    return err?.error?.message || "Operazione non riuscita. Riprova.";
  }
}
